import AdvancedController from "./advanced-controller";
import { Breakpoints } from "../utils/breakpoints";
import { debounce } from "../utils/utils";
import Hunt from "huntjs";
import Swiper, { Navigation, Pagination, Autoplay, Thumbs } from "swiper";

const AUTO_HEIGHT_BREAKPOINT = "sm";
const AUTO_HEIGHT_BREAKPOINT_FOR_1_SLIDE = "md";

export default class SwiperController extends AdvancedController {
  static targets = ["swiper", "thumbs"];

  static values = {
    autoplay: Boolean,
    sameheight: Boolean,
    onlyautoheightmobile: Boolean,
    maxslides: Boolean,
  };

  _slidesPerView;
  _autoplay;
  _autoplayDelay;
  _loop;
  _totalSlides;
  _slidesCloned;
  _spaceBetween;
  _space;
  _thumbs;
  _initialSlide;

  connect() {
    super.connect();

    const swiperContainer = this.swiperTarget;
    if (!swiperContainer) {
      console.error("Swiper target not found");
      return;
    }

    this._slidesPerView = swiperContainer.getAttribute("data-slides-per-view") || 1;
    this._autoplay = !!+swiperContainer.getAttribute("data-autoplay-enable");
    this._autoplayDelay = swiperContainer.getAttribute("data-autoplay-delay");
    this._loop = !!+swiperContainer.getAttribute("data-loop-enable");
    this._initialSlide = swiperContainer.getAttribute("data-initial-slide");
    this._spaceBetween = swiperContainer.getAttribute("data-space-between");
    this._space = swiperContainer.getAttribute("data-space");
    this._thumbs = swiperContainer.getAttribute("data-thumbs") === "1";
    this._totalSlides = swiperContainer.querySelectorAll(".swiper-slide");
    this._slidesCloned = false;

    if (this._thumbs) {
      const thumbsElement = swiperContainer.querySelector(".thumbs-slider");
      if (thumbsElement) {
        this.thumbsSwiper = this.createThumbsSlider(thumbsElement);
        if (!this.thumbsSwiper) {
          console.error("Failed to initialize thumbs swiper.");
        }
      } else {
        console.error("Thumbnail slider container not found.");
      }
    }

    this.createSlider(swiperContainer, !this.sameheightValue);

    this.breakpoints = new Breakpoints();

    if (this.hasSwiperTarget && this.onlyautoheightmobileValue) {
      this.manageAutoHeight();
      window.addEventListener("resize", () =>
        debounce(this.manageAutoHeight(), 250)
      );
    }

    this.disableAutoswipe();
    new Hunt(this.element, {
      persist: true,
      enter: () => this.enableAutoswipe(),
      leave: () => this.disableAutoswipe(),
    });
  }

  enableAutoswipe() {
    if (
      this.hasSwiperTarget &&
      this.autoplayValue &&
      !this.swiperTarget.swiper.autoplay.running
    ) {
      this.swiperTarget.swiper.autoplay.start();
    }
  }

  disableAutoswipe() {
    if (
      this.hasSwiperTarget &&
      this.autoplayValue &&
      this.swiperTarget.swiper &&
      this.swiperTarget.swiper.autoplay.running
    ) {
      this.swiperTarget.swiper.autoplay.stop();
    }
  }

  manageAutoHeight() {
    const breakpoint = this._slidesPerView > 1 ? AUTO_HEIGHT_BREAKPOINT : AUTO_HEIGHT_BREAKPOINT_FOR_1_SLIDE;

    if (
      this.hasSwiperTarget &&
      this.breakpoints.breakpointDown(breakpoint) &&
      !this.swiperTarget.swiper.params.autoHeight
    ) {
      this.destroyAndReinit(true);
    }
    if (
      this.hasSwiperTarget &&
      this.breakpoints.breakpointUp(breakpoint) &&
      this.swiperTarget.swiper.params.autoHeight
    ) {
      this.destroyAndReinit(false);
    }
  }

  destroyAndReinit(autoHeight) {

    if (this.swiperTarget.querySelector('.thumbs-slider')) {
      return;
    } else {
      if (this.swiperTarget.swiper) {
        this.swiperTarget.swiper.destroy();
        this._slidesCloned = false;
      }
      this.createSlider(this.swiperTarget, autoHeight);
    }
  }

  createSlider(element, autoHeight = false) {
    const config = {
      modules: [Navigation, Pagination, Autoplay, Thumbs],
      slidesPerView: this._slidesPerView,
      pagination: {
        el: element.querySelector('.swiper-pagination'),
        bulletActiveClass: 'current',
        clickable: true,
        renderBullet: function (index, className) {
          return '<span class="' + className + '">' + (index + 1) + "</span>";
        },

      },
      grabCursor: true,
      navigation: {
        prevEl: element.querySelector(".swiper-button-prev"),
        nextEl: element.querySelector(".swiper-button-next"),
      },
    };

    if (this._autoplay) {
      config.autoplay = {
        delay: Number(this._autoplayDelay),
        pauseOnMouseEnter: true,
        disableOnInteraction: false,
      };
    }

    if (this._spaceBetween === "") {
      if (this._space) {
        config.spaceBetween = this._space
      } else {
        config.spaceBetween = 24;
      }
    }

    if (this._slidesPerView > 1) {
      const swiperBreakpoints = this.calculateSwiperBreakpoints(this._slidesPerView);
      config.breakpoints = swiperBreakpoints;
      config.centeredSlides = false;
    }

    if (this._loop) {
      config.loop = true;
    }

    // turn off pagination bc cloned slides would show up as well
    if (this._slidesCloned) {
      config.pagination = false;
    }

    if (autoHeight) {
      config.autoHeight = true;
    }

    if (this._thumbs) { // Prüfen, ob der Thumbnail-Slider erfolgreich erstellt wurde
      config.thumbs = {
        swiper: this.thumbsSwiper,
      };
    }

    if (this._thumbs && this.thumbsSwiper) {
      config.thumbs = { swiper: this.thumbsSwiper };
    } else if (this._thumbs) {
      console.error("Thumbs swiper not initialized.");
    }

    this.swiperInstance = new Swiper(element, config);

    if (this._initialSlide) {
      this.swiperInstance.slideTo(this._initialSlide);
    }
  }

  createThumbsSlider(element) {
    return new Swiper(element, {
      slidesPerView: 4,
      freeMode: true,
      watchSlidesProgress: true,
      spaceBetween: 10,
    });
  }

  calculateSwiperBreakpoints(slidesPerView) {
    const swiperBreakpoints = {};
    const breakpointsService = new Breakpoints();
    const allBreakpoints = breakpointsService.getAll();
    delete allBreakpoints.xl;

    const perBreakpoint = slidesPerView / (Object.keys(allBreakpoints).length + 1);
    const breakpointPixelValues = Object.values(allBreakpoints).sort((a, b) => a - b);
    let i = 0.6;

    swiperBreakpoints[0] = { slidesPerView: 1 };
    for (const breakpointSize of breakpointPixelValues) {
      i++;
      const slidesPerBreakpoint = Math.floor(1 + i * perBreakpoint);
      swiperBreakpoints[breakpointSize] = {
        slidesPerView: this.getSlidesPerBreakpoint(slidesPerBreakpoint, slidesPerView),
      };
    }

    return swiperBreakpoints;
  }

  slideTo(index) {
    if (this.hasSwiperTarget && this.swiperTarget.swiper) {
      this.swiperTarget.swiper.slideTo(index);
    }
  }

  getSlidesPerBreakpoint(slidesPerBreakpoint, slidesPerView) {
    if (this.maxslidesValue && slidesPerBreakpoint > slidesPerView) {
      slidesPerBreakpoint = slidesPerView;
    }

    if (this._loop && !this._slidesCloned && this._totalSlides.length <= slidesPerBreakpoint * 2) {
      this.handleLoop();
    }

    return slidesPerBreakpoint;
  }

  handleLoop() {
    this._totalSlides.forEach((slide) => {
      this.swiperTarget.querySelector(".swiper-wrapper").appendChild(slide.cloneNode(true));
    });
    this._slidesCloned = true;
  }
}
